import {
  CLUB_COLORS,
  CLUB_FOUNDING_GRANT,
  CLUB_LOGO_TYPES,
  FACULTIES,
  MAX_CLUB_BLURB,
  MAX_CLUB_CODE,
  MAX_CLUB_LOGO_BYTES,
  MAX_CLUB_NAME,
  MIN_CLUB_CODE,
  MIN_CLUB_NAME,
  clubCodeProblem,
  clubColorProblem,
  clubNameProblem,
  suggestClubCode,
  type ClubProposalDto,
} from "@clubap/shared";
import { useEffect, useState } from "react";
import { fetchMyProposal, proposeClub, withdrawProposal } from "../social/api.js";

interface Props {
  /** Se mandó la solicitud. `App` cierra el panel y avisa a quien corresponda. */
  onFounded: () => void;
  onClose: () => void;
}

/** Cuánto pesa un texto DE VERDAD: `.length` cuenta unidades UTF-16, no bytes. */
function bytesDe(fuente: string): number {
  return new TextEncoder().encode(fuente).length;
}

/**
 * Qué clase de logo es este archivo, o `null` si no es ninguna de las dos.
 *
 * Se mira el `type` del archivo primero y la extensión después porque hay sistemas que
 * entregan un `type` vacío para `.svg` —no está registrado en el equipo— y rechazar ahí
 * un SVG perfectamente válido se vive como "mi logo no se puede subir".
 *
 * Esto NO es la validación: el servidor mira los bytes y no le cree a nadie. Acá sólo se
 * evita el viaje y se puede decir por qué antes de que salga.
 */
function tipoDeLogo(file: File): string | null {
  const admitidos = CLUB_LOGO_TYPES as readonly string[];
  if (admitidos.includes(file.type)) return file.type;

  const nombre = file.name.toLowerCase();
  if (nombre.endsWith(".svg")) return "image/svg+xml";
  if (nombre.endsWith(".png")) return "image/png";
  return null;
}

function comoDataUri(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("No se pudo leer el archivo."));
    reader.readAsDataURL(file);
  });
}

/**
 * Una URL que un `<img>` pueda cargar a partir de la FUENTE de un logo.
 *
 * Un PNG llega como data URI y ya es una URL. Un SVG llega como texto, y ahí está la
 * razón de este hook: envolverlo en un `Blob` es lo que permite dibujarlo dentro de un
 * `<img>` en vez de incrustarlo en el documento. La diferencia no es de estilo — un SVG
 * incrustado ejecuta el script que traiga, y estos los sube cualquier jugador; dentro de
 * un `<img>` el navegador lo trata como imagen y no ejecuta nada.
 *
 * El objeto se revoca al cambiar de logo y al desmontar. Sin eso el blob se queda en
 * memoria hasta que se recarga la página, y el panel del admin crea uno por solicitud.
 */
function useLogoUrl(logo: string | null): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!logo) {
      setUrl(null);
      return;
    }

    if (logo.startsWith("data:")) {
      setUrl(logo);
      return;
    }

    const objeto = URL.createObjectURL(new Blob([logo], { type: "image/svg+xml" }));
    setUrl(objeto);
    return () => URL.revokeObjectURL(objeto);
    // Depende del TEXTO del logo, no de un objeto que lo envuelva: una referencia nueva
    // en cada render revocaría y recrearía el blob sin que el logo hubiera cambiado.
  }, [logo]);

  return url;
}

/**
 * La cara de un club que todavía no existe: su logo propuesto, o sus siglas.
 *
 * `ClubMark` no sirve para esto y no es un descuido suyo: aquél dibuja el logo pidiéndolo
 * por su URL (`/api/clubs/<id>/logo`), y un club que aún no ha nacido no tiene id
 * que poner ahí. Acá la fuente la tenemos en la mano.
 *
 * Lo usan el formulario —para la vista previa— y la bandeja del admin, que tiene que ver
 * lo que va a aprobar. Vive en este archivo porque es el que convierte un archivo en esa
 * fuente; la bandeja sólo la recibe ya hecha.
 */
export function LogoPreview({
  logo,
  code,
  size = 32,
}: {
  logo: string | null;
  code: string;
  size?: number;
}) {
  const url = useLogoUrl(logo);
  const style = { width: size, height: size };

  if (!url) {
    return (
      <span className="club-marca__siglas" style={style} aria-hidden="true">
        {code}
      </span>
    );
  }

  return (
    <img
      className="club-marca__logo"
      style={style}
      src={url}
      alt=""
      aria-hidden="true"
      draggable={false}
    />
  );
}

/** El logo elegido, ya leído. Se guarda la fuente porque es lo que viaja y lo que se mide. */
interface LogoElegido {
  source: string;
  type: string;
  fileName: string;
}

/**
 * Fundar un club: el formulario y el estado de lo que ya mandaste.
 *
 * Son dos pantallas en un panel a propósito. Una solicitud abierta es el motivo por el
 * que no puedes mandar otra, así que enseñar el formulario deshabilitado sería contestar
 * a medias: lo que quien vuelve necesita saber es en qué quedó la suya y cómo retirarla.
 */
export function FoundClubPanel({ onFounded, onClose }: Props) {
  const [proposal, setProposal] = useState<ClubProposalDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  /* Las siglas dejan de seguir al nombre en cuanto alguien las escribe a mano: seguir
     pisándolas borraría lo que acaba de teclear en la siguiente letra del nombre. */
  const [codeTocado, setCodeTocado] = useState(false);
  const [blurb, setBlurb] = useState("");
  const [color, setColor] = useState<string>(CLUB_COLORS[0] ?? "#8fd0ff");
  const [facultyId, setFacultyId] = useState<string | null>(null);
  const [logo, setLogo] = useState<LogoElegido | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);

  const siglas = codeTocado ? code : suggestClubCode(name.trim());

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const mia = await fetchMyProposal();
        if (!cancelled) setProposal(mia);
      } catch (cause) {
        if (!cancelled) {
          setError(cause instanceof Error ? cause.message : "No se pudo consultar tu solicitud.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const elegirLogo = async (file: File | null) => {
    setLogoError(null);

    if (!file) {
      setLogo(null);
      return;
    }

    const type = tipoDeLogo(file);
    if (!type) {
      setLogoError("El logo tiene que ser un SVG o un PNG. Otros formatos no se admiten.");
      return;
    }

    try {
      /* El SVG se lee como TEXTO y el PNG como data URI: son las dos formas que el
         servidor guarda, y medir el tope sobre cualquier otra cosa mediría otra cosa. */
      const source = type === "image/svg+xml" ? await file.text() : await comoDataUri(file);
      const pesa = bytesDe(source);

      if (pesa > MAX_CLUB_LOGO_BYTES) {
        setLogoError(
          `Ese logo pesa ${Math.ceil(pesa / 1024)} KB y el tope son ${Math.floor(
            MAX_CLUB_LOGO_BYTES / 1024,
          )} KB. Prueba con un dibujo más simple o una imagen más pequeña.`,
        );
        return;
      }

      setLogo({ source, type, fileName: file.name });
    } catch {
      setLogoError("No se pudo leer ese archivo.");
    }
  };

  /** Lo que está mal escrito, en español, o null si todo cuadra. Mismas reglas que el servidor. */
  const problema = (): string | null => {
    const enNombre = clubNameProblem(name);
    if (enNombre === "empty") return "Ponle un nombre al club.";
    if (enNombre === "too_short") return `El nombre necesita al menos ${MIN_CLUB_NAME} letras.`;
    if (enNombre === "too_long") return `El nombre no puede pasar de ${MAX_CLUB_NAME} letras.`;

    const enSiglas = clubCodeProblem(siglas);
    if (enSiglas === "empty") return "Las siglas no pueden quedar vacías.";
    if (enSiglas === "too_short") return `Las siglas necesitan al menos ${MIN_CLUB_CODE} letras.`;
    if (enSiglas === "too_long") return `Las siglas no pueden pasar de ${MAX_CLUB_CODE} letras.`;

    if (clubColorProblem(color) !== null) return "Elige uno de los colores de la fila.";

    return null;
  };

  const enviar = async () => {
    const mal = problema();
    if (mal) {
      setError(mal);
      return;
    }

    setBusy(true);
    setError(null);

    try {
      await proposeClub({
        code: siglas.trim(),
        name: name.trim(),
        blurb: blurb.trim(),
        color,
        facultyId,
        logo: logo?.source ?? null,
        logoType: logo?.type ?? null,
      });
      onFounded();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No se pudo enviar la solicitud.");
    } finally {
      setBusy(false);
    }
  };

  const retirar = async () => {
    if (!proposal) return;

    setBusy(true);
    setError(null);

    try {
      await withdrawProposal(proposal.id);
      setProposal(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No se pudo retirar la solicitud.");
    } finally {
      setBusy(false);
    }
  };

  const esperando = proposal !== null && proposal.status === "pendiente";

  return (
    <div className="overlay">
      <div className="panel sheet club-sheet">
        <div className="sheet__head">
          <h2 className="sheet__title">Fundar un club</h2>
          <button className="icon-btn" type="button" onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </div>

        {error && (
          <p className="sheet__error" role="alert">
            {error}
          </p>
        )}

        {loading && <p className="sheet__hint">Viendo si ya mandaste una…</p>}

        {!loading && esperando && proposal && (
          <section className="sheet__group">
            <h3 className="sheet__slot">Tu solicitud</h3>

            <div className="club-prop">
              <div className="club-prop__cara">
                <span
                  className="badge club-marca"
                  style={{ color: proposal.color, borderColor: proposal.color }}
                >
                  <LogoPreview logo={proposal.logo} code={proposal.code} size={34} />
                </span>
                <span className="club-prop__texto">
                  <span className="club-siglas">{proposal.code}</span>
                  <span className="sheet__name">{proposal.name}</span>
                  {proposal.blurb && <span className="social__blurb">{proposal.blurb}</span>}
                </span>
                <span className="sheet__badge">esperando</span>
              </div>
            </div>

            <div className="club-acciones">
              <button className="btn btn--small" type="button" disabled={busy} onClick={() => void retirar()}>
                {busy ? "…" : "Retirar la solicitud"}
              </button>
            </div>

            <p className="sheet__hint">
              Un administrador la revisa. Si la retiras puedes mandar otra distinta cuando quieras.
            </p>
          </section>
        )}

        {!loading && !esperando && (
          <>
            {proposal && proposal.status === "rechazada" && (
              <p className="sheet__hint">
                Tu solicitud anterior no salió. Puedes mandar otra: cambia lo que creas que
                hizo falta.
              </p>
            )}

            <form
              className="club-forma"
              onSubmit={(event) => {
                event.preventDefault();
                void enviar();
              }}
            >
              <label className="club-campo">
                <span className="sheet__slot">Nombre completo</span>
                <input
                  className="join__input"
                  value={name}
                  maxLength={MAX_CLUB_NAME}
                  placeholder="Asociación de Estudiantes de…"
                  onChange={(event) => setName(event.target.value)}
                />
              </label>

              <label className="club-campo">
                <span className="sheet__slot">Siglas</span>
                <input
                  className="join__input"
                  value={siglas}
                  maxLength={MAX_CLUB_CODE}
                  placeholder="AEFIEC"
                  onChange={(event) => {
                    setCodeTocado(true);
                    setCode(event.target.value.toUpperCase());
                  }}
                />
                <span className="sheet__hint club-ayuda">
                  Es como se le dice al club en el campus, y lo único que cabe en la insignia.
                  Se proponen solas a partir del nombre.
                </span>
              </label>

              <label className="club-campo">
                <span className="sheet__slot">De qué va</span>
                <input
                  className="join__input"
                  value={blurb}
                  maxLength={MAX_CLUB_BLURB}
                  placeholder="Una línea para quien lo vea en el directorio"
                  onChange={(event) => setBlurb(event.target.value)}
                />
              </label>

              <div className="club-campo">
                <span className="sheet__slot">Color</span>
                <div className="club-colores">
                  {CLUB_COLORS.map((opcion) => (
                    <button
                      className={`club-color${opcion === color ? " club-color--on" : ""}`}
                      key={opcion}
                      type="button"
                      style={{ background: opcion }}
                      aria-label={`Color ${opcion}`}
                      aria-pressed={opcion === color}
                      onClick={() => setColor(opcion)}
                    />
                  ))}
                </div>
              </div>

              <label className="club-campo">
                <span className="sheet__slot">Facultad</span>
                <select
                  className="join__input"
                  value={facultyId ?? ""}
                  onChange={(event) => setFacultyId(event.target.value || null)}
                >
                  {/* Transversal va primero y es el valor por defecto: un club nuevo no
                      tiene por qué ser de una facultad, y elegir una decide dónde está
                      su sede. */}
                  <option value="">Transversal (de todo el campus)</option>
                  {FACULTIES.map((faculty) => (
                    <option key={faculty.id} value={faculty.id}>
                      {faculty.code} · {faculty.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="club-campo">
                <span className="sheet__slot">Logo (opcional)</span>
                <input
                  className="join__input club-archivo"
                  type="file"
                  accept=".svg,image/svg+xml,.png,image/png"
                  onChange={(event) => {
                    void elegirLogo(event.target.files?.[0] ?? null);
                  }}
                />
                {logoError && (
                  <span className="sheet__error club-ayuda" role="alert">
                    {logoError}
                  </span>
                )}
                {logo && <span className="sheet__hint club-ayuda">{logo.fileName}</span>}
                {!logo && !logoError && (
                  <span className="sheet__hint club-ayuda">
                    SVG o PNG, hasta {Math.floor(MAX_CLUB_LOGO_BYTES / 1024)} KB. Sin logo el
                    club lleva sus siglas, que es un estado normal y no un hueco.
                  </span>
                )}
              </label>

              <div className="club-vista">
                <span
                  className="badge club-marca"
                  style={{ color, borderColor: color }}
                  title="Así se verá su insignia"
                >
                  <LogoPreview logo={logo?.source ?? null} code={siglas || "··"} size={34} />
                </span>
                <span className="club-prop__texto">
                  <span className="club-siglas">{siglas || "··"}</span>
                  <span className="sheet__name">{name.trim() || "Sin nombre todavía"}</span>
                </span>
              </div>

              <div className="club-acciones">
                <button className="btn" type="submit" disabled={busy}>
                  {busy ? "…" : "Mandar la solicitud"}
                </button>
              </div>
            </form>

            <p className="sheet__hint">
              La aprueba un administrador. Si sale, el club nace con ◉ {CLUB_FOUNDING_GRANT} en su
              caja y tú lo presides.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
